import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { adminApi, getToken, clearToken } from '../services/adminApi';

interface AdminUser {
  username: string;
  role: string;
}

interface LoginResult {
  ok: boolean;
  message?: string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  // Có token sẵn (F5) → xác thực lại qua /me
  useEffect(() => {
    let active = true;
    if (!getToken()) {
      setLoading(false);
      return;
    }
    adminApi
      .me()
      .then((res) => {
        if (!active) return;
        if (res.success && res.data) setUser(res.data);
        else logout();
      })
      .catch(() => {
        if (active) logout();
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [logout]);

  // Token hết hạn giữa chừng (adminApi bắn event khi 401) → tự logout
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener('admin-unauthorized', handler);
    return () => window.removeEventListener('admin-unauthorized', handler);
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    try {
      const res = await adminApi.login(username, password);
      if (res.success && res.data) {
        setUser(res.data.user);
        return { ok: true };
      }
      return { ok: false, message: res.message || 'Đăng nhập thất bại' };
    } catch (err) {
      return { ok: false, message: (err as Error).message || 'Đăng nhập thất bại' };
    }
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
